import React, { useEffect, useState, useMemo } from 'react';
import { Grid, CircularProgress, Box, MenuItem, Select, FormControl, InputLabel, TextField, Stack, Button, Snackbar, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PageContainer from '../../../components/container/PageContainer';
import Breadcrumb from '../../../layouts/full-layout/breadcrumb/Breadcrumb';
import { apiGetAdicionalesTaller } from '../../../apis';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportToCSV } from '../../../apis/utils';

const AdicionalesTaller = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Filter state
  const [filters, setFilters] = useState({
    taller: [],
    año: [],
    mes: [],
    promedioMin: '',
  });

  // Extract unique values for select filters, sorted
  const uniqueTaller = useMemo(() => [...new Set(data.map(row => row.taller))].sort((a, b) => a.localeCompare(b, 'es')), [data]);
  const uniqueAño = useMemo(() => [...new Set(data.map(row => row['año']))].sort((a, b) => Number(a) - Number(b)), [data]);
  const uniqueMes = useMemo(() => [...new Set(data.map(row => row['mes']))].sort((a, b) => Number(a) - Number(b)), [data]);

  // Filtering logic
  const filteredData = useMemo(() => {
    return data.filter(row => {
      if (filters.taller.length > 0 && !filters.taller.includes(row.taller)) return false;
      if (filters.año.length > 0 && !filters.año.includes(String(row['año']))) return false;
      if (filters.mes.length > 0 && !filters.mes.includes(String(row['mes']))) return false;
      if (filters.promedioMin && Number(row['Promedio por pedido']) < Number(filters.promedioMin)) return false;
      return true;
    });
  }, [data, filters]);

  const columns = [
    {
      field: 'taller',
      headerName: 'Taller',
      flex: 1,
      disableColumnMenu: true,
    },
    {
      field: 'año',
      headerName: 'Año',
      width: 100,
      type: 'number',
      disableColumnMenu: true,
    },
    {
      field: 'mes',
      headerName: 'Mes',
      width: 100,
      type: 'number',
      disableColumnMenu: true,
    },
    {
      field: 'N° OT',
      headerName: 'N° OT',
      flex: 1,
      type: 'number',
      disableColumnMenu: true,
    },
    {
      field: 'Adicionales Ofrecidos',
      headerName: 'Adicionales Ofrecidos',
      flex: 1,
      type: 'number',
      disableColumnMenu: true,
    },
    {
      field: 'Promedio por pedido',
      headerName: 'Promedio por pedido',
      flex: 1,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return typeof value === 'number' && !isNaN(value)
          ? value.toFixed(2)
          : value || '';
      },
      disableColumnMenu: false, // Only rightmost column
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiGetAdicionalesTaller();
        const formattedData = response.map((item, index) => ({
          id: index + 1,
          ...item
        }));
        setData(formattedData);
        setError(null);
      } catch (err) {
        setError('Error al cargar los datos');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      // Format current date as YYYY-MM-DD
      const today = new Date().toISOString().split('T')[0];
      const filename = `adicionales-taller-${today}`;
      
      await exportToCSV(filteredData, columns, filename);
      
      setSnackbar({
        open: true,
        message: 'Archivo exportado exitosamente',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error al exportar CSV:', error);
      setSnackbar({
        open: true,
        message: 'Error al exportar el archivo',
        severity: 'error'
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (error) {
    return (
      <PageContainer title="Reportes" description="Reporte de Adicionales por Taller">
        <Breadcrumb
          title={'Reporte de Adicionales por Taller'}
          items={[
            {
              to: '/home',
              title: 'HOME',
            },
          ]}
        />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          {error}
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Reportes" description="Reporte de Adicionales por Taller">
      <Breadcrumb
        title={'Reporte de Adicionales por Taller'}
        items={[
          {
            to: '/home',
            title: 'HOME',
          },
        ]}
      />

      <Grid container spacing={0}>
        <Grid item xs={12} lg={12}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ 
              height: 'calc(100vh - 200px)', 
              width: '100%',
              mb: 8, // Increased margin bottom for footer
              overflowX: 'auto', // Enable horizontal scroll if needed
              '& .MuiDataGrid-root': {
                width: '100%',
                minHeight: '100%',
              },
              '& .MuiDataGrid-main': {
                width: '100%',
              },
              '& .MuiDataGrid-virtualScroller': {
                width: '100%',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                width: '100%',
              },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                width: '100%',
              }
            }}>
              {/* Filter Row */}
              <Stack direction="row" spacing={2} sx={{ mb: 2, p: 1, background: '#f5f5f5', borderRadius: 2 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Taller</InputLabel>
                  <Select
                    label="Taller"
                    multiple
                    value={filters.taller}
                    onChange={e => setFilters(f => ({ ...f, taller: e.target.value }))}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Todos';
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} seleccionados`;
                    }}
                  >
                    {uniqueTaller.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Año</InputLabel>
                  <Select
                    label="Año"
                    multiple
                    value={filters.año}
                    onChange={e => setFilters(f => ({ ...f, año: e.target.value }))}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Todos';
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} seleccionados`;
                    }}
                  >
                    {uniqueAño.map(opt => (
                      <MenuItem key={opt} value={String(opt)}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Mes</InputLabel>
                  <Select
                    label="Mes"
                    multiple
                    value={filters.mes}
                    onChange={e => setFilters(f => ({ ...f, mes: e.target.value }))}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'Todos';
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} seleccionados`;
                    }}
                  >
                    {uniqueMes.map(opt => (
                      <MenuItem key={opt} value={String(opt)}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Promedio mínimo"
                  size="small"
                  type="number"
                  value={filters.promedioMin}
                  onChange={e => setFilters(f => ({ ...f, promedioMin: e.target.value }))}
                  sx={{ width: 140 }}
                />
                <Button
                  variant="contained"
                  startIcon={exportLoading ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={exportLoading}
                  sx={{ 
                    ml: 'auto',
                    backgroundColor: 'rgba(26, 151, 245, 0.08)',
                    color: '#222',
                    '&:hover': {
                      backgroundColor: 'rgba(26, 151, 245, 0.15)',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(26, 151, 245, 0.04)',
                      color: 'rgba(34, 34, 34, 0.5)'
                    }
                  }}
                >
                  {exportLoading ? 'Exportando...' : 'Exportar CSV'}
                </Button>
              </Stack>
              <DataGrid
                rows={filteredData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                  sorting: {
                    sortModel: [
                      { field: 'year', sort: 'desc' },
                      { field: 'month', sort: 'desc' },
                      { field: 'taller', sort: 'asc' }
                    ],
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                autoHeight
                density="comfortable"
                slots={{
                  loadingOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ),
                }}
                sx={{
                  '& .MuiDataGrid-cell:focus': {
                    outline: 'none',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'rgba(26, 151, 245, 0.08)',
                    color: '#222',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                  },
                  '& .MuiDataGrid-row': {
                    transition: 'background 0.2s',
                    '&:hover': {
                      backgroundColor: '#f0f4fa',
                    },
                  },
                  '& .MuiDataGrid-root': {
                    borderRadius: 8,
                  },
                  '& .MuiDataGrid-cell': {
                    padding: '12px',
                  },
                }}
              />
            </Box>
          )}
        </Grid>
      </Grid>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            bgcolor: snackbar.severity === 'success' ? 'green' : 'darkred',
            color: 'white'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default AdicionalesTaller;
